import React, { useState, useEffect } from 'react';
import styles from './HouseModal.module.css';

const HouseModal = ({ house, onClose, onNext, onPrev, hasNext, hasPrev }) => {
  const [showFloorplan, setShowFloorplan] = useState(false);

  // Reset floorplan view when house changes
  useEffect(() => {
    setShowFloorplan(false);
  }, [house]);

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (house) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [house]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'ArrowRight' && hasNext) {
        onNext();
      } else if (event.key === 'ArrowLeft' && hasPrev) {
        onPrev();
      }
    };

    if (house) {
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [house, onClose, onNext, onPrev, hasNext, hasPrev]);

  if (!house) return null;

  const currentImage = showFloorplan ? house.floorplan : house.image;
  const fallbackImage = 'https://placehold.co/800x600/222/fff?text=No+Image';
  const hasFloorplan = house.floorplan && house.floorplan !== house.image;

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return new Date(timestamp).toLocaleDateString('en-GB');
    } catch (e) {
      return 'Invalid Date';
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}> {/* Close on overlay click */}
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}> {/* Prevent closing when clicking inside content */}
        <button className={styles.closeButton} onClick={onClose}>&times;</button>

        <div className={styles.imageSection}>
          <img
            src={currentImage || fallbackImage}
            alt={showFloorplan ? 'Floorplan' : 'House image'}
            className={styles.modalImage}
            onError={(e) => { e.target.onerror = null; e.target.src = fallbackImage; }}
          />
          {hasFloorplan && (
            <button
              onClick={() => setShowFloorplan(!showFloorplan)}
              className={styles.imageToggleButton}
            >
              {showFloorplan ? 'Show Front View' : 'Show Floorplan'}
            </button>
          )}
        </div>

        <div className={styles.detailsSection}>
          <h2 className={styles.modalTitle}>
            {house.bedrooms} bed {house['property type']} near {house.station_name || house.station}
          </h2>
          <p className={styles.modalPrice}>£{house.price ? house.price.toLocaleString() : 'N/A'}</p>
          <p className={styles.modalDateAdded}>Added: {formatDate(house.date_added)}</p>
          
          <div className={styles.modalHouseDetails}>
             <span>{house.bedrooms || 'N/A'} beds</span>
             <span>{house.bathrooms || 'N/A'} baths</span>
             <span>{house.size ? `${house.size.toLocaleString()} sqft` : 'N/A size'}</span>
             <span>{house.distance_to_station ? `${house.distance_to_station.toFixed(2)} miles to station` : 'N/A dist'}</span>
             <span>{house.tenure || 'N/A tenure'}</span>
             {house.journey1_duration && (
                <span className={styles.journeyTime}>➡️ PC1: {house.journey1_duration} mins</span>
             )}
             {house.journey2_duration && (
                <span className={styles.journeyTime}>➡️ PC2: {house.journey2_duration} mins</span>
             )}
          </div>

          <h3 className={styles.descriptionTitle}>Description</h3>
          <div className={styles.modalDescription} dangerouslySetInnerHTML={{ __html: house.description || 'No description available.' }}></div>

          <a
            href={house.property_url}
            target="_blank"
            rel="noopener noreferrer"
            className={`${styles.rightmoveButton} ${!house.property_url ? styles.disabledButton : ''}`}
          >
            View on Rightmove
          </a>
        </div>

        {/* Navigation Buttons */}
        {hasPrev && (
          <button className={`${styles.navButton} ${styles.prevButton}`} onClick={onPrev}>&larr; Prev</button>
        )}
        {hasNext && (
          <button className={`${styles.navButton} ${styles.nextButton}`} onClick={onNext}>Next &rarr;</button>
        )}
      </div>
    </div>
  );
};

export default HouseModal; 